import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { SERVICE_CATEGORIES } from '../../../config/ieumConfig';
import Pagination from '../../../components/common/Pagination';
import {
  getAllComplaints,
  updateComplaint,
  getMonitoringComplaints,
  COMPLAINT_CATEGORIES,
} from '../../../services/complaintService';
import { useAuth } from '../../../context/AuthContext';

const STATUS_META = {
  pending: { label: '대기', color: '#F59E0B' },
  processing: { label: '처리중', color: '#3B82F6' },
  resolved: { label: '완료', color: '#22C55E' },
  dismissed: { label: '반려', color: '#9CA3AF' },
};

const PER_PAGE = 20;

const ComplaintManage = () => {
  const { userData } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('list');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState([]);
  const [modal, setModal] = useState(null);
  const [modalStatus, setModalStatus] = useState('pending');
  const [modalMemo, setModalMemo] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalComplaintCategory, setModalComplaintCategory] = useState('');
  const [modalPenalty, setModalPenalty] = useState('');
  const [modalPenaltyReason, setModalPenaltyReason] = useState('');
  const [modalError, setModalError] = useState('');

  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) setStatusFilter(s);
  }, [searchParams]);

  const [riskPage, setRiskPage] = useState(1);
  const [monitoringPage, setMonitoringPage] = useState(1);
  const [monitoringData, setMonitoringData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const results = await getAllComplaints();
      setData(results);
    } catch (err) {
      console.error('민원 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchMonitoring = async () => {
    try {
      const results = await getMonitoringComplaints();
      setMonitoringData(results);
    } catch (err) {
      console.error('모니터링 로딩 실패:', err);
    }
  };

  useEffect(() => {
    if (tab === 'monitoring') fetchMonitoring();
  }, [tab]);

  const pagedMonitoring = monitoringData.slice((monitoringPage - 1) * PER_PAGE, monitoringPage * PER_PAGE);
  const totalMonitoringPages = Math.max(1, Math.ceil(monitoringData.length / PER_PAGE));

  // 업체 리스크 집계
  const partnerRisk = useMemo(() => {
    const map = new Map();
    data.forEach((c) => {
      const key = c.partnerId || c.partnerName || '-';
      if (!map.has(key)) {
        map.set(key, { id: key, partnerName: c.partnerName || '-', count: 0, categories: new Set() });
      }
      const entry = map.get(key);
      entry.count += 1;
      if (c.category) entry.categories.add(SERVICE_CATEGORIES[c.category]?.label || c.category);
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [data]);

  const filteredList = useMemo(() => {
    if (statusFilter === 'all') return data;
    return data.filter((c) => c.status === statusFilter);
  }, [data, statusFilter]);
  const paged = filteredList.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(filteredList.length / PER_PAGE));

  const pagedRisk = partnerRisk.slice((riskPage - 1) * PER_PAGE, riskPage * PER_PAGE);
  const totalRiskPages = Math.max(1, Math.ceil(partnerRisk.length / PER_PAGE));

  const pendingCount = data.filter((c) => c.status === 'pending' || c.status === 'processing').length;

  const openModal = async (c) => {
    setModal(c);
    setModalStatus(c.status || 'pending');
    setModalMemo(c.followUpMemo || '');
    setModalTitle(c.summary || '');
    setModalComplaintCategory(c.complaintCategory || '');
    setModalPenalty(typeof c.penalty === 'number' ? String(c.penalty) : '');
    setModalPenaltyReason(c.penaltyReason || '');
    setModalError('');
    // partnerName 누락 시 service_requests → partners 순으로 fallback 조회
    if (!c.partnerName && c.serviceRequestId) {
      try {
        const srSnap = await getDoc(doc(db, 'service_requests', c.serviceRequestId));
        if (!srSnap.exists()) return;
        const sr = srSnap.data();
        let partnerName = sr.partnerName || '';
        const pid = sr.assigned_partner_id || sr.assignedPartnerId;
        if (!partnerName && pid) {
          const pSnap = await getDoc(doc(db, 'partners', pid));
          if (pSnap.exists()) {
            const p = pSnap.data();
            partnerName = p.business_name || p.businessName || p.company_name || p.name || '';
          }
        }
        if (partnerName) setModal((prev) => (prev ? { ...prev, partnerName } : prev));
      } catch {}
    }
  };

  const handleSave = async () => {
    if (!modal) return;
    setModalError('');
    // 처리 완료/반려 시 벌점 점수 필수 (담당자 확정안)
    const needsPenalty = (modalStatus === 'resolved' || modalStatus === 'dismissed');
    const penaltyNum = Number(modalPenalty);
    if (needsPenalty && (isNaN(penaltyNum) || modalPenalty === '')) {
      setModalError('처리 완료/반려 시 벌점 점수 입력은 필수입니다. (0점도 입력)');
      return;
    }
    try {
      await updateComplaint(modal.id, {
        status: modalStatus,
        followUpMemo: modalMemo,
        followUpBy: userData?.uid || '',
        complaintCategory: modalComplaintCategory || undefined,
        penalty: isNaN(penaltyNum) ? 0 : penaltyNum,
        penaltyReason: modalPenaltyReason,
      });
      setModal(null);
      fetchData();
      if (tab === 'monitoring') fetchMonitoring();
    } catch (err) {
      console.error('민원 저장 실패:', err);
      setModalError('저장에 실패했습니다.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (paged.every((r) => selectedIds.includes(r.id))) {
      setSelectedIds((prev) => prev.filter((id) => !paged.some((r) => r.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...paged.map((r) => r.id)])));
    }
  };

  const handleBulkResolve = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`선택한 ${selectedIds.length}건을 처리완료로 변경할까요?`)) return;
    try {
      for (const id of selectedIds) {
        await updateComplaint(id, { status: 'resolved', followUpMemo: '일괄 처리완료', followUpBy: userData?.uid || '' });
      }
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      console.error('일괄 처리 실패:', err);
    }
  };

  const handleExport = () => {
    const rows = [
      ['접수일', '고객명', '업체명', '카테고리', '민원 요약', '상태'],
      ...data.map((c) => [
        c.createdAt || '',
        c.customerName || '',
        c.partnerName || '',
        SERVICE_CATEGORIES[c.category]?.label || '',
        c.summary || '',
        STATUS_META[c.status]?.label || '',
      ]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `complaints_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const StatusText = ({ status }) => {
    const s = STATUS_META[status];
    if (!s) return <span>-</span>;
    return <span style={{ color: s.color, fontWeight: 500, fontSize: 13 }}>{s.label}</span>;
  };

  return (
    <Container>
      <TopRow>
        <Title>고객 민원 관리</Title>
        <TopActions>
          <TopBtn onClick={handleExport}>CSV</TopBtn>
          <TopBtn onClick={fetchData}>새로고침</TopBtn>
        </TopActions>
      </TopRow>

      <Tabs>
        <TabBtn $active={tab === 'list'} onClick={() => setTab('list')}>
          민원 목록 <TabCount>({data.length})</TabCount>
        </TabBtn>
        <TabBtn $active={tab === 'monitoring'} onClick={() => setTab('monitoring')}>
          요청 모니터링 <TabCount>({monitoringData.length})</TabCount>
        </TabBtn>
        <TabBtn $active={tab === 'risk'} onClick={() => setTab('risk')}>
          업체 리스크 <TabCount>({partnerRisk.length})</TabCount>
        </TabBtn>
      </Tabs>

      {tab === 'list' && (
        <>
          <ActionBar>
            <SelectAllLabel>
              <input
                type="checkbox"
                checked={paged.length > 0 && paged.every((r) => selectedIds.includes(r.id))}
                onChange={toggleSelectAll}
              />
              전체 선택 ({selectedIds.length}건)
            </SelectAllLabel>
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="all">상태 전체</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </Select>
            <ActionBtn onClick={handleBulkResolve}>일괄 처리완료</ActionBtn>
            <RightMuted>대기 · 처리중 {pendingCount}건</RightMuted>
          </ActionBar>

          {loading ? (
            <EmptyText>로딩 중...</EmptyText>
          ) : paged.length === 0 ? (
            <EmptyText>접수된 민원이 없습니다.</EmptyText>
          ) : (
            <CardList>
              {paged.map((c) => (
                <ComplaintCard key={c.id}>
                  <CardLeft>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => toggleSelect(c.id)}
                    />
                  </CardLeft>
                  <CardBody onClick={() => openModal(c)}>
                    <CardTopRow>
                      <CardDate>{c.createdAt || '-'}</CardDate>
                      <StatusText status={c.status} />
                    </CardTopRow>
                    <CardTitle>
                      {c.customerName || '고객'} · {SERVICE_CATEGORIES[c.category]?.label || '기타'}
                    </CardTitle>
                    <CardSummary>{c.summary || c.content || '내용 없음'}</CardSummary>
                    <CardMeta>
                      {c.partnerName && <MetaItem>업체: {c.partnerName}</MetaItem>}
                      {c.followUpMemo && <MetaItem>후속조치: {c.followUpMemo}</MetaItem>}
                    </CardMeta>
                  </CardBody>
                </ComplaintCard>
              ))}
            </CardList>
          )}

          <PageFoot>
            <PageInfo>페이지 {page} / {totalPages} · 총 {filteredList.length}건</PageInfo>
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </PageFoot>
        </>
      )}

      {tab === 'monitoring' && (
        <>
          <MonitoringNote>
            <b>요청 모니터링</b> — 운영자가 집중 관리해야 하는 3종: 일정 변경(schedule_change) · 재매칭(rematch) · 최종 취소(final_cancel)
          </MonitoringNote>
          {pagedMonitoring.length === 0 ? (
            <EmptyText>모니터링 대상 민원이 없습니다.</EmptyText>
          ) : (
            <CardList>
              {pagedMonitoring.map((c) => (
                <ComplaintCard key={c.id}>
                  <CardBody onClick={() => openModal(c)} style={{ paddingLeft: 12 }}>
                    <CardTopRow>
                      <CardDate>{c.createdAt || '-'}</CardDate>
                      <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 600 }}>
                        {COMPLAINT_CATEGORIES[c.complaintCategory] || c.complaintCategory}
                      </span>
                      <StatusText status={c.status} />
                    </CardTopRow>
                    <CardTitle>
                      {c.customerName || '고객'} · {SERVICE_CATEGORIES[c.category]?.label || '-'}
                    </CardTitle>
                    <CardSummary>{c.summary || c.content || '내용 없음'}</CardSummary>
                    <CardMeta>
                      {c.partnerName && <MetaItem>업체: {c.partnerName}</MetaItem>}
                      {typeof c.penalty === 'number' && c.penalty > 0 && (
                        <MetaItem style={{ color: '#EF4444', fontWeight: 600 }}>벌점: {c.penalty}점</MetaItem>
                      )}
                    </CardMeta>
                  </CardBody>
                </ComplaintCard>
              ))}
            </CardList>
          )}
          <PageFoot>
            <PageInfo>페이지 {monitoringPage} / {totalMonitoringPages} · 총 {monitoringData.length}건</PageInfo>
            <Pagination current={monitoringPage} total={totalMonitoringPages} onChange={setMonitoringPage} />
          </PageFoot>
        </>
      )}

      {tab === 'risk' && (
        <>
          {partnerRisk.length === 0 ? (
            <EmptyText>리스크가 집계된 업체가 없습니다.</EmptyText>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>업체명</Th>
                    <Th>서비스 종류</Th>
                    <Th style={{ textAlign: 'right' }}>민원 건수</Th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRisk.map((p) => (
                    <Tr key={p.id}>
                      <Td>{p.partnerName}</Td>
                      <Td>{Array.from(p.categories).join(', ') || '-'}</Td>
                      <Td style={{ textAlign: 'right', color: '#EF4444', fontWeight: 600 }}>{p.count}건</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
          <PageFoot>
            <PageInfo>페이지 {riskPage} / {totalRiskPages} · 총 {partnerRisk.length}개 업체</PageInfo>
            <Pagination current={riskPage} total={totalRiskPages} onChange={setRiskPage} />
          </PageFoot>
        </>
      )}

      {modal && (
        <ModalOverlay onClick={() => setModal(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>후속조치 기록</ModalTitle>
              <XBtn onClick={() => setModal(null)}><IoClose size={22} /></XBtn>
            </ModalHeader>
            <ModalBody>
              <InfoRow>
                <InfoItem><InfoLabel>고객</InfoLabel><InfoValue>{modal.customerName || '-'}</InfoValue></InfoItem>
                <InfoItem><InfoLabel>업체</InfoLabel><InfoValue>{modal.partnerName || '-'}</InfoValue></InfoItem>
                <InfoItem><InfoLabel>카테고리</InfoLabel><InfoValue>{SERVICE_CATEGORIES[modal.category]?.label || '-'}</InfoValue></InfoItem>
                <InfoItem><InfoLabel>접수일</InfoLabel><InfoValue>{modal.createdAt || '-'}</InfoValue></InfoItem>
              </InfoRow>

              <FieldLabel style={{ marginTop: 18 }}>민원 제목</FieldLabel>
              <Input value={modalTitle} onChange={(e) => setModalTitle(e.target.value)} />

              <FieldLabel style={{ marginTop: 14 }}>민원 분류</FieldLabel>
              <Select value={modalComplaintCategory} onChange={(e) => setModalComplaintCategory(e.target.value)}>
                <option value="">— 선택 —</option>
                {Object.entries(COMPLAINT_CATEGORIES).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
                <option value="final_cancel">최종 취소 (final_cancel)</option>
              </Select>

              <FieldLabel style={{ marginTop: 14 }}>처리 상태</FieldLabel>
              <Select value={modalStatus} onChange={(e) => setModalStatus(e.target.value)}>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Select>

              <FieldLabel style={{ marginTop: 14 }}>
                벌점 점수 {(modalStatus === 'resolved' || modalStatus === 'dismissed') && <RequiredMark>*</RequiredMark>}
              </FieldLabel>
              <Input
                type="number"
                min="0"
                step="1"
                value={modalPenalty}
                onChange={(e) => setModalPenalty(e.target.value)}
                placeholder="0 이상의 정수 (누적 15점 이상 시 파트너 자동 정지)"
              />
              {modalPenalty !== '' && Number(modalPenalty) > 0 && (
                <>
                  <FieldLabel style={{ marginTop: 10 }}>벌점 사유</FieldLabel>
                  <Input
                    value={modalPenaltyReason}
                    onChange={(e) => setModalPenaltyReason(e.target.value)}
                    placeholder="벌점 부여 사유 (선택)"
                  />
                </>
              )}

              <FieldLabel style={{ marginTop: 14 }}>처리 내용</FieldLabel>
              <TextArea
                rows={4}
                value={modalMemo}
                onChange={(e) => setModalMemo(e.target.value)}
                placeholder="조치 내용을 기록하세요."
              />

              {modalError && <ErrorNote>{modalError}</ErrorNote>}
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={() => setModal(null)}>취소</GhostBtn>
              <PrimaryBtn onClick={handleSave}>저장</PrimaryBtn>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ComplaintManage;

// --- Styled ---
const Container = styled.div``;
const TopRow = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;`;
const Title = styled.h1`font-size:18px;font-weight:600;margin:0;`;
const TopActions = styled.div`display:flex;gap:6px;`;
const TopBtn = styled.button`padding:6px 12px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:12px;color:${({theme})=>theme.colors.text};cursor:pointer;&:hover{background:#F9FAFB;}`;

const Tabs = styled.div`display:flex;gap:8px;border-bottom:1px solid ${({theme})=>theme.colors.border};margin-bottom:16px;`;
const TabBtn = styled.button`
  padding:10px 18px;border:none;background:none;
  font-size:13px;font-weight:${({$active})=>$active?600:400};
  color:${({$active,theme})=>$active?theme.colors.primary:theme.colors.textSecondary};
  border-bottom:2px solid ${({$active,theme})=>$active?theme.colors.primary:'transparent'};
  margin-bottom:-1px;cursor:pointer;
`;
const TabCount = styled.span`font-size:12px;color:${({theme})=>theme.colors.textSecondary};font-weight:400;margin-left:2px;`;

const ActionBar = styled.div`display:flex;gap:12px;align-items:center;margin-bottom:12px;`;
const SelectAllLabel = styled.label`display:flex;align-items:center;gap:6px;font-size:13px;color:${({theme})=>theme.colors.text};cursor:pointer;`;
const ActionBtn = styled.button`padding:7px 14px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:13px;color:${({theme})=>theme.colors.text};cursor:pointer;&:hover{background:#F9FAFB;}`;
const RightMuted = styled.div`margin-left:auto;font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;

const CardList = styled.div`display:flex;flex-direction:column;gap:8px;`;
const ComplaintCard = styled.div`
  display:flex;gap:12px;align-items:flex-start;
  background:#fff;padding:14px 16px;
  border-radius:${({theme})=>theme.radius.md};
  border:1px solid ${({theme})=>theme.colors.borderLight};
`;
const CardLeft = styled.div`padding-top:2px;`;
const CardBody = styled.div`flex:1;cursor:pointer;`;
const CardTopRow = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;`;
const CardDate = styled.span`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;
const CardTitle = styled.div`font-size:14px;font-weight:600;color:#1F2937;margin-bottom:4px;`;
const CardSummary = styled.div`font-size:13px;color:${({theme})=>theme.colors.text};margin-bottom:6px;line-height:1.5;`;
const CardMeta = styled.div`display:flex;gap:14px;flex-wrap:wrap;`;
const MetaItem = styled.span`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;

const EmptyText = styled.p`text-align:center;padding:56px 0;font-size:14px;color:${({theme})=>theme.colors.textSecondary};background:#fff;border-radius:${({theme})=>theme.radius.md};`;

const TableWrapper = styled.div`overflow-x:auto;background:#fff;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`background:#F9FAFB;padding:12px 14px;text-align:left;font-size:12px;font-weight:500;color:${({theme})=>theme.colors.textSecondary};border-bottom:1px solid ${({theme})=>theme.colors.border};white-space:nowrap;`;
const Tr = styled.tr`&:not(:last-child) td{border-bottom:1px solid ${({theme})=>theme.colors.borderLight};}`;
const Td = styled.td`padding:12px 14px;font-size:13px;color:${({theme})=>theme.colors.text};white-space:nowrap;`;

const PageFoot = styled.div`display:flex;align-items:center;justify-content:space-between;`;
const PageInfo = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;

const ModalOverlay = styled.div`position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:1000;`;
const ModalContent = styled.div`background:#fff;border-radius:${({theme})=>theme.radius.lg};width:100%;max-width:480px;max-height:90vh;overflow-y:auto;box-shadow:${({theme})=>theme.shadow.lg};`;
const ModalHeader = styled.div`display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid ${({theme})=>theme.colors.border};`;
const ModalTitle = styled.h2`font-size:16px;font-weight:600;margin:0;`;
const XBtn = styled.button`background:none;border:none;color:${({theme})=>theme.colors.textLight};cursor:pointer;display:inline-flex;align-items:center;`;
const ModalBody = styled.div`padding:22px;`;
const ModalFooter = styled.div`display:flex;justify-content:flex-end;gap:8px;padding:14px 22px;border-top:1px solid ${({theme})=>theme.colors.border};`;
const InfoRow = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:12px;`;
const InfoItem = styled.div``;
const InfoLabel = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:2px;`;
const InfoValue = styled.div`font-size:13px;color:#1F2937;font-weight:500;`;
const FieldLabel = styled.div`font-size:13px;font-weight:500;color:${({theme})=>theme.colors.text};margin-bottom:6px;`;
const Input = styled.input`width:100%;padding:9px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;
const Select = styled.select`width:100%;padding:9px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;background:#fff;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;
const TextArea = styled.textarea`width:100%;padding:10px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;resize:vertical;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;
const GhostBtn = styled.button`padding:8px 18px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:13px;cursor:pointer;`;
const PrimaryBtn = styled.button`padding:8px 18px;border:none;background:${({theme})=>theme.colors.primary};color:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:13px;font-weight:500;cursor:pointer;`;
const RequiredMark = styled.span`color:#EF4444;margin-left:2px;`;
const ErrorNote = styled.div`margin-top:12px;padding:10px 12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:${({theme})=>theme.radius.sm};color:#B91C1C;font-size:12px;`;
const MonitoringNote = styled.div`padding:12px 14px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:${({theme})=>theme.radius.sm};color:#92400E;font-size:13px;line-height:1.5;margin-bottom:14px;b{font-weight:600;}`;
